import {
  Component,
  h,
  Prop,
  Watch,
  Event,
  EventEmitter,
  ComponentInterface,
  getMode,
  State,
  Listen,
  Fragment
} from '@stencil/core';
import { ISocialItem } from './ds-social-links/ds-social-links';
import { INavigationItem } from './ds-footer-navigation/ds-footer-navigation';
import { jsonWatcher, emitCustomEvent } from '../../utils/utils';

export interface ISocialLinks {
  title: string;
  items: Array<ISocialItem>;
}

@Component({
  tag: 'ds-footer',
  styleUrls: {
    default: 'ds-footer.dark.scss',
    light: 'ds-footer.light.scss'
  },
  shadow: true
})
export class DsFooter implements ComponentInterface {
  @Prop() navigationItems: string;
  private _navigationItems: Array<INavigationItem> = [];

  @Prop() socialLinks: string;
  private _socialLinks: Array<ISocialItem> = [];

  @Prop() socialLinksTitle: string;
  private _socialLinksTitle: string;

  @Prop() isShowCookiePreferences: boolean;
  @Prop() isLightTheme: boolean;
  @Prop() mode?: 'light' | 'default';

  @State() isMobile: boolean = false;

  @Event({ eventName: 'footerDidLoad' }) footerComponentDidLoad: EventEmitter;

  @Watch('navigationItems')
  @Watch('socialLinks')
  @Watch('socialLinksTitle')
  private _updateProps(): void {
    this._propWatcher('navigationItems', this.navigationItems);
    this._propWatcher('socialLinks', this.socialLinks);
    this._propWatcher('socialLinksTitle', this.socialLinksTitle);
  }

  private mobileBreakpoint: number = 993;

  componentWillLoad(): void {
    this._updateProps();
    this.updateMobileState();
  }

  componentDidLoad(): void {
    this.footerComponentDidLoad.emit();
    emitCustomEvent('cxsdsFooterComponentReady');
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.updateMobileState();
  }

  private isDeviceMobile(): boolean {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= this.mobileBreakpoint;
    }
    return false;
  }

  private updateMobileState(): void {
    const mobile = this.isDeviceMobile();
    if (this.isMobile !== mobile) {
      this.isMobile = mobile;
    }
  }

  private _propWatcher<T>(prop: string, value: T): void {
    const resolved = jsonWatcher(prop, value);
    if (resolved.value) {
      this[`_${resolved.prop}`] = resolved.value;
    }
  }

  render() {
    const socialLinksSection = (
      <div class='ds-footer__social-links'>
        <ds-social-links socialLinks={this._socialLinks} headline={this._socialLinksTitle} />
      </div>
    );

    const navigationSection = !!this._navigationItems.length && (
      <ds-footer-navigation
        navigationItems={this._navigationItems}
        isShowCookieInFooter={this.isShowCookiePreferences}
      />
    );

    return (
      <footer
        class={{
          'ds-footer': true
        }}
        data-mode={getMode(this)}
      >
        <div class='ds-footer__container'>
          {this.isMobile ? (
            <Fragment>
              {socialLinksSection}
              {navigationSection}
            </Fragment>
          ) : (
            <Fragment>
              {navigationSection}
              {socialLinksSection}
            </Fragment>
          )}
        </div>
      </footer>
    );
  }
}
